import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { IimCardModule } from '@iim-angular/components';
import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { BC } from '@lib/shared/breadcrumbs/constants';
import { BreadcrumbService } from '@lib/shared/breadcrumbs/service';
import { BootstrapColors, ConfirmationService, DialogOptions } from '@lib/util';
import { BaseSiteLayoutModule } from '@lib/util/base-site-layout';
import { TranslateModule } from '@ngx-translate/core';
import { finalize, firstValueFrom, forkJoin } from 'rxjs';
import { PurchasingGroupAssignment } from '../models/purchasing-group-assignment.model';
import { PurchasingGroupAssignableUser } from '../models/purchasing-group-assignable-user.model';
import { PurchasingGroupDetail } from '../models/purchasing-group-detail.model';
import { PurchasingGroupMember } from '../models/purchasing-group-member.model';
import { PurchasingGroupDataService } from '../services/purchasing-group-data.service';
import {
    ICompanyCodeGroup,
    buildCompanyCodeGroups,
    buildMembers,
    sortAssignableUsersByName,
} from './purchasing-group-detail.utils';
import {
    PurchasingGroupCompanyCodeDialogComponent,
    PurchasingGroupCompanyCodeDialogData,
    PurchasingGroupCompanyCodeDialogResult,
} from './company-code/purchasing-group-company-code-dialog.component';
import {
    PurchasingGroupMembersDialogComponent,
    PurchasingGroupMembersDialogData,
    PurchasingGroupMembersDialogResult,
} from './members/purchasing-group-members-dialog.component';
import {
    PurchasingGroupMasterDataDialogComponent,
    PurchasingGroupMasterDataDialogData,
    PurchasingGroupMasterDataDialogResult,
} from './master-data/purchasing-group-master-data-dialog.component';
import {
    PurchasingGroupMaterialGroupsDialogComponent,
    PurchasingGroupMaterialGroupsDialogData,
    PurchasingGroupMaterialGroupsDialogResult,
} from './material-groups/purchasing-group-material-groups-dialog.component';
import {
    PurchasingGroupBulkMaterialGroupsDialogComponent,
    PurchasingGroupBulkMaterialGroupsDialogData,
    PurchasingGroupBulkMaterialGroupsDialogResult,
} from './bulk-material-groups/purchasing-group-bulk-material-groups-dialog.component';

@Component({
    selector: 'lib-purchasing-group-detail',
    standalone: true,
    imports: [CommonModule, BaseSiteLayoutModule, TranslateModule, IimCardModule],
    templateUrl: './purchasing-group-detail.component.html',
    styleUrls: ['./purchasing-group-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasingGroupDetailComponent implements OnInit {
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly dataService = inject(PurchasingGroupDataService);
    private readonly dialog = inject(MatDialog);
    private readonly notificationService = inject(NotificationService);
    private readonly route = inject(ActivatedRoute);

    public readonly defaultHeading = marker('Einkäufergruppen');
    public readonly heading = computed(() => this.purchasingGroup()?.name ?? null);
    public readonly icon = 'fas fa-users';

    public readonly loading = signal(false);
    public readonly assignments = signal<readonly PurchasingGroupAssignment[]>([]);
    public readonly assignmentsLoading = signal(false);
    public readonly members = signal<readonly PurchasingGroupMember[]>([]);
    public readonly memberCount = computed(() => this.members().length);
    public readonly membersLoading = signal(false);
    public readonly purchasingGroup = signal<PurchasingGroupDetail | null>(null);
    public readonly statusActionPending = signal(false);
    private readonly allActiveUsers = signal<readonly PurchasingGroupAssignableUser[]>([]);
    private readonly collapsedCompanyCodeGroupIds = signal<ReadonlySet<string>>(new Set());

    public readonly companyCodeGroups = computed<ReadonlyArray<ICompanyCodeGroup>>(() => {
        const detail = this.purchasingGroup();
        if (!detail) return [];
        return buildCompanyCodeGroups(detail, this.assignments());
    });

    public readonly hasNoCompanyCodes = computed(
        () => (this.purchasingGroup()?.assignedCompanyCodes.length ?? 0) === 0,
    );

    constructor() {
        this.breadcrumbService.set(BC.SYSTEM.LIST, BC.SYSTEM.PURCHASING_GROUPS.LIST);
    }

    public ngOnInit(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (!id) {
            this.notificationService.showNotification(
                marker('Fehler beim Laden der Daten'),
                NotificationMessageLevel.ERROR,
            );
            return;
        }

        this.load(id);
        this.loadAssignments(id);
        this.loadMembers(id);
    }

    public isCompanyCodeGroupExpanded(companyCodeId: string): boolean {
        return !this.collapsedCompanyCodeGroupIds().has(companyCodeId);
    }

    public toggleCompanyCodeGroup(companyCodeId: string): void {
        this.collapsedCompanyCodeGroupIds.update((current) => {
            const next = new Set(current);
            if (next.has(companyCodeId)) {
                next.delete(companyCodeId);
            } else {
                next.add(companyCodeId);
            }
            return next;
        });
    }

    public deactivate(): void {
        const purchasingGroup = this.purchasingGroup();
        if (!purchasingGroup || !purchasingGroup.active || this.statusActionPending()) {
            return;
        }
        this.deactivatePurchasingGroup(purchasingGroup.id, false);
    }

    public reactivate(): void {
        const purchasingGroup = this.purchasingGroup();
        if (!purchasingGroup || purchasingGroup.active || this.statusActionPending()) {
            return;
        }

        this.statusActionPending.set(true);
        this.dataService.reactivate(purchasingGroup.id).subscribe({
            next: (updated) => {
                this.statusActionPending.set(false);
                this.purchasingGroup.set(updated);
                this.setBreadcrumb(updated);
                this.notificationService.showNotification(
                    marker('Einkäufergruppe wurde aktiviert'),
                    NotificationMessageLevel.SUCCESS,
                );
            },
            error: (error) => {
                this.statusActionPending.set(false);
                const message = this.dataService.resolveErrorMessage(
                    error,
                    marker('Einkäufergruppe konnte nicht aktiviert werden'),
                );
                this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
            },
        });
    }

    public async openMasterData(): Promise<void> {
        const purchasingGroup = this.purchasingGroup();
        if (!purchasingGroup) return;

        const dialogData = new PurchasingGroupMasterDataDialogData(purchasingGroup);
        const dialogRef = this.dialog.open<
            PurchasingGroupMasterDataDialogComponent,
            PurchasingGroupMasterDataDialogData,
            PurchasingGroupMasterDataDialogResult
        >(PurchasingGroupMasterDataDialogComponent, DialogOptions.MEDIUM(dialogData));
        const result = await firstValueFrom(dialogRef.afterClosed());
        if (result?.updated) {
            this.load(purchasingGroup.id);
        }
    }

    public async openCompanyCode(): Promise<void> {
        const purchasingGroup = this.purchasingGroup();
        if (!purchasingGroup) return;

        const dialogData = new PurchasingGroupCompanyCodeDialogData(
            purchasingGroup.id,
            purchasingGroup.assignedCompanyCodes.map((cc) => cc.id),
        );
        const dialogRef = this.dialog.open<
            PurchasingGroupCompanyCodeDialogComponent,
            PurchasingGroupCompanyCodeDialogData,
            PurchasingGroupCompanyCodeDialogResult
        >(PurchasingGroupCompanyCodeDialogComponent, DialogOptions.MEDIUM(dialogData));
        await firstValueFrom(dialogRef.afterClosed());
        // Dialog persists each change directly, so reload regardless of how it was closed
        // (X, backdrop, ESC) to reflect any changes made while it was open.
        this.load(purchasingGroup.id);
        this.loadAssignments(purchasingGroup.id);
    }

    /**
     * EPROC-11379: opens the bulk dialog to assign material groups to several company codes at once.
     */
    public async openBulkMaterialGroups(): Promise<void> {
        const purchasingGroup = this.purchasingGroup();
        if (!purchasingGroup) return;

        const dialogData = new PurchasingGroupBulkMaterialGroupsDialogData(
            purchasingGroup.id,
            purchasingGroup.assignedCompanyCodes.map((cc) => ({ id: cc.id, code: cc.code, name: cc.name })),
        );
        const dialogRef = this.dialog.open<
            PurchasingGroupBulkMaterialGroupsDialogComponent,
            PurchasingGroupBulkMaterialGroupsDialogData,
            PurchasingGroupBulkMaterialGroupsDialogResult
        >(PurchasingGroupBulkMaterialGroupsDialogComponent, DialogOptions.LARGE(dialogData));
        const result = await firstValueFrom(dialogRef.afterClosed());
        if (result?.updated) {
            this.load(purchasingGroup.id);
            this.loadAssignments(purchasingGroup.id);
        }
    }

    public async openMaterialGroups(group: ICompanyCodeGroup): Promise<void> {
        const purchasingGroup = this.purchasingGroup();
        if (!purchasingGroup) return;

        const dialogData = new PurchasingGroupMaterialGroupsDialogData(
            purchasingGroup.id,
            group.companyCodeId,
            group.companyCode,
            group.companyCodeName,
            group.assignments,
            group.isOrphan,
        );
        const dialogRef = this.dialog.open<
            PurchasingGroupMaterialGroupsDialogComponent,
            PurchasingGroupMaterialGroupsDialogData,
            PurchasingGroupMaterialGroupsDialogResult
        >(PurchasingGroupMaterialGroupsDialogComponent, DialogOptions.LARGE(dialogData));
        const result = await firstValueFrom(dialogRef.afterClosed());
        if (result?.updated) {
            this.loadAssignments(purchasingGroup.id);
        }
    }

    public async openMembersDialog(): Promise<void> {
        const purchasingGroup = this.purchasingGroup();
        if (!purchasingGroup || this.membersLoading()) return;

        const dialogData = new PurchasingGroupMembersDialogData(
            purchasingGroup.id,
            this.members(),
            this.allActiveUsers(),
        );
        const dialogRef = this.dialog.open<
            PurchasingGroupMembersDialogComponent,
            PurchasingGroupMembersDialogData,
            PurchasingGroupMembersDialogResult
        >(PurchasingGroupMembersDialogComponent, DialogOptions.LARGE(dialogData));
        const result = await firstValueFrom(dialogRef.afterClosed());
        if (result?.updated) {
            this.loadMembers(purchasingGroup.id);
            this.notificationService.showNotification(
                marker('Mitglieder wurden gespeichert'),
                NotificationMessageLevel.SUCCESS,
            );
        }
    }

    private load(id: string): void {
        this.loading.set(true);
        this.dataService
            .getById(id)
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (purchasingGroup) => {
                    this.purchasingGroup.set(purchasingGroup);
                    this.setBreadcrumb(purchasingGroup);
                },
                error: (error) => {
                    const message = this.dataService.resolveErrorMessage(
                        error,
                        marker('Fehler beim Laden der Daten'),
                    );
                    this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                },
            });
    }

    private loadAssignments(id: string): void {
        this.assignmentsLoading.set(true);
        this.dataService
            .getAssignments(id)
            .pipe(finalize(() => this.assignmentsLoading.set(false)))
            .subscribe({
                next: (assignments) => this.assignments.set(assignments),
                error: (error) => {
                    const message = this.dataService.resolveErrorMessage(
                        error,
                        marker('Zuständigkeiten konnten nicht geladen werden.'),
                    );
                    this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                },
            });
    }

    private loadMembers(id: string): void {
        this.membersLoading.set(true);
        forkJoin({
            members: this.dataService.getMembers(id),
            users: this.dataService.getAssignableUsers(),
        })
            .pipe(finalize(() => this.membersLoading.set(false)))
            .subscribe({
                next: ({ members, users }) => {
                    this.allActiveUsers.set(sortAssignableUsersByName(users.filter((user) => user.active)));
                    this.members.set(buildMembers(members.customerIds, users));
                },
                error: (error) => {
                    const message = this.dataService.resolveErrorMessage(
                        error,
                        marker('Mitglieder konnten nicht geladen werden.'),
                    );
                    this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                },
            });
    }

    private deactivatePurchasingGroup(id: string, confirmed: boolean): void {
        this.statusActionPending.set(true);
        this.dataService.deactivate(id, confirmed).subscribe({
            next: (updated) => {
                this.statusActionPending.set(false);
                this.purchasingGroup.set(updated);
                this.setBreadcrumb(updated);
                this.notificationService.showNotification(
                    marker('Einkäufergruppe wurde deaktiviert'),
                    NotificationMessageLevel.SUCCESS,
                );
            },
            error: (error) => {
                this.statusActionPending.set(false);
                if (!confirmed && this.dataService.isDeactivationConfirmationRequired(error)) {
                    void this.confirmDeactivation(id);
                    return;
                }

                const message = this.dataService.resolveErrorMessage(
                    error,
                    marker('Einkäufergruppe konnte nicht deaktiviert werden'),
                );
                this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
            },
        });
    }

    private async confirmDeactivation(id: string): Promise<void> {
        const confirmed = await firstValueFrom(
            this.confirmationService.confirm(
                marker('Für diese Einkäufergruppe bestehen noch aktive Zuordnungen. Trotzdem deaktivieren?'),
                {
                    heading: marker('Einkäufergruppe deaktivieren'),
                    confirmationButtonText: marker('Deaktivieren'),
                    confirmationButtonColor: BootstrapColors.DANGER,
                },
            ),
        );

        if (confirmed) {
            this.deactivatePurchasingGroup(id, true);
        }
    }

    private setBreadcrumb(purchasingGroup: PurchasingGroupDetail): void {
        this.breadcrumbService.set(
            BC.SYSTEM.LIST,
            BC.SYSTEM.PURCHASING_GROUPS.LIST,
            BC.SYSTEM.PURCHASING_GROUPS.DETAIL(purchasingGroup.name, purchasingGroup.id),
        );
    }
}
